import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Container, Form, Spinner, Table } from "react-bootstrap";
import { Copy, RefreshCw, Search } from "lucide-react";
import { CallAPIGetPromise } from "Common/commaon";
import "./twilio-notifications.css";

type TwilioNotification = {
  id: number;
  job_id?: number;
  job_title?: string;
  job_city?: string;
  job_state?: string;
  homeowner_first_name?: string;
  homeowner_last_name?: string;
  homeowner_email?: string;
  contractor_email?: string;
  contractor_phone: string;
  category?: string;
  sms_sid?: string;
  sms_status: string;
  sms_error?: string;
  call_sid?: string;
  call_status: string;
  call_error?: string;
  call_duration?: number;
  created_at: string;
  updated_at: string;
};

type TwilioStats = {
  total?: number;
  sms_sent?: number;
  sms_failed?: number;
  calls_started?: number;
  calls_completed?: number;
  calls_failed?: number;
  calls_skipped?: number;
};

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function titleCase(value = "") {
  return value.replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function statusBadge(status = "") {
  const normalized = status.toLowerCase();
  if (["sent", "completed", "answered"].includes(normalized)) return "success";
  if (["failed", "undelivered", "busy", "no-answer", "canceled"].includes(normalized)) return "danger";
  if (["queued", "initiated", "ringing", "in-progress"].includes(normalized)) return "warning";
  if (normalized === "skipped") return "secondary";
  return "light";
}

function StatCard({ label, value, tone = "primary" }: { label: string; value: any; tone?: string }) {
  return (
    <Card className={`twilio-stat twilio-stat--${tone}`}>
      <Card.Body>
        <div className="text-muted small">{label}</div>
        <div className={`fs-3 fw-semibold text-${tone}`}>{Number(value || 0).toLocaleString()}</div>
      </Card.Body>
    </Card>
  );
}

function normalizeTwilioResponse(response: any) {
  // The shared API client already unwraps Axios responses, so the normal
  // payload is { data: [...], stats: {...} }. Keep the other shapes for
  // compatibility without unwrapping the records array a second time.
  const body = response || {};
  const payload = Array.isArray(body)
    ? { data: body, stats: {} }
    : Array.isArray(body?.data)
      ? body
      : Array.isArray(body?.data?.data)
        ? body.data
        : Array.isArray(body?.result)
          ? { data: body.result, stats: body.stats }
          : { data: [], stats: body?.stats || body?.data?.stats || {} };
  return {
    rows: Array.isArray(payload.data) ? payload.data : [],
    stats: payload.stats || {},
  };
}

function TechnicalId({ value, error, label }: { value?: string; error?: string; label: string }) {
  if (error) return <div className="twilio-technical-error" title={error}>{error}</div>;
  if (!value) return <span className="twilio-technical-empty">No ID</span>;
  const shortValue = value.length > 18 ? `${value.slice(0, 7)}…${value.slice(-7)}` : value;
  return (
    <button
      type="button"
      className="twilio-technical-id"
      title={`Copy ${label}: ${value}`}
      aria-label={`Copy ${label}`}
      onClick={() => void navigator.clipboard?.writeText(value)}
    >
      <span>{shortValue}</span>
      <Copy aria-hidden="true" />
    </button>
  );
}

const PAGE_SIZE = 25;

export default function TwilioNotifications() {
  const [rows, setRows] = useState<TwilioNotification[]>([]);
  const [stats, setStats] = useState<TwilioStats>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [smsStatus, setSmsStatus] = useState("all");
  const [callStatus, setCallStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const loadRows = async () => {
    setLoading(true);
    setError("");
    try {
      const response: any = await CallAPIGetPromise("/twilio-v2/job-notifications?limit=500");
      const normalized = normalizeTwilioResponse(response);
      setRows(normalized.rows);
      setStats(normalized.stats);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to load Twilio notifications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (smsStatus !== "all" && row.sms_status !== smsStatus) return false;
      if (callStatus !== "all" && row.call_status !== callStatus) return false;
      if (!query) return true;
      const homeowner = [row.homeowner_first_name, row.homeowner_last_name].filter(Boolean).join(" ");
      return [
        row.id,
        row.job_id,
        row.job_title,
        row.category,
        homeowner,
        row.homeowner_email,
        row.contractor_email,
        row.contractor_phone,
        row.sms_sid,
        row.call_sid,
        row.sms_error,
        row.call_error,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [callStatus, rows, search, smsStatus]);

  useEffect(() => {
    setCurrentPage(1);
  }, [callStatus, search, smsStatus]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const visibleRows = filteredRows.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div className="page-content twilio-notifications-page">
      <Container fluid>
        <div className="twilio-page-toolbar">
            <Button variant="light" onClick={loadRows} disabled={loading}>
              <RefreshCw size={16} className="me-1" /> Refresh
            </Button>
        </div>

        {error ? <Alert variant="danger">{error}</Alert> : null}

        <div className="twilio-stats-grid">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="SMS Sent" value={stats.sms_sent} tone="success" />
          <StatCard label="SMS Failed" value={stats.sms_failed} tone="danger" />
          <StatCard label="Calls Started" value={stats.calls_started} tone="warning" />
          <StatCard label="Calls Completed" value={stats.calls_completed} tone="success" />
          <StatCard label="Calls Skipped" value={stats.calls_skipped} tone="secondary" />
        </div>

        <Card className="twilio-filters-card mb-3">
          <Card.Body>
            <div className="twilio-filter-grid">
              <label className="twilio-filter-field twilio-filter-field--search">
                <span>Search</span>
                <div className="app-search-field">
                  <Search className="app-field-icon" />
                  <Form.Control
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Job, contractor, phone, ID, or error"
                  />
                </div>
              </label>
              <label className="twilio-filter-field">
                <span>SMS status</span>
                <Form.Select value={smsStatus} onChange={(event) => setSmsStatus(event.target.value)}>
                  <option value="all">All SMS statuses</option>
                  <option value="pending">Pending</option>
                  <option value="sent">Sent</option>
                  <option value="failed">Failed</option>
                  <option value="skipped">Skipped</option>
                </Form.Select>
              </label>
              <label className="twilio-filter-field">
                <span>Call status</span>
                <Form.Select value={callStatus} onChange={(event) => setCallStatus(event.target.value)}>
                  <option value="all">All call statuses</option>
                  <option value="pending">Pending</option>
                  <option value="queued">Queued</option>
                  <option value="initiated">Initiated</option>
                  <option value="ringing">Ringing</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="skipped">Skipped</option>
                </Form.Select>
              </label>
              <div className="twilio-result-count">
                <strong>{filteredRows.length}</strong>
                <span>{filteredRows.length === 1 ? "record" : "records"}</span>
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card className="twilio-log-card">
          <Card.Body>
            {loading ? (
              <div className="py-5 text-center"><Spinner animation="border" /></div>
            ) : filteredRows.length === 0 ? (
              <Alert variant="light" className="mb-0">No Twilio notifications found.</Alert>
            ) : (
              <div className="table-responsive">
                <Table hover className="twilio-log-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Contractor</th>
                      <th>SMS</th>
                      <th>Call</th>
                      <th>Duration</th>
                      <th>Created</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => {
                      const homeowner = [row.homeowner_first_name, row.homeowner_last_name].filter(Boolean).join(" ");
                      return (
                        <tr key={row.id}>
                          <td>
                            <div className="fw-semibold">#{row.job_id || "—"} {row.job_title || "Untitled job"}</div>
                            <div className="small text-muted">{row.category || "General"} {row.job_city ? `• ${row.job_city}, ${row.job_state || ""}` : ""}</div>
                            <div className="small text-muted">Homeowner: {homeowner || row.homeowner_email || "—"}</div>
                          </td>
                          <td>
                            <div className="fw-semibold">{row.contractor_email || "Contractor"}</div>
                            <div className="small text-muted">{row.contractor_phone || "—"}</div>
                          </td>
                          <td>
                            <Badge bg={statusBadge(row.sms_status)} text={statusBadge(row.sms_status) === "light" ? "dark" : undefined}>
                              {titleCase(row.sms_status)}
                            </Badge>
                            <TechnicalId value={row.sms_sid} error={row.sms_error} label="SMS ID" />
                          </td>
                          <td>
                            <Badge bg={statusBadge(row.call_status)} text={statusBadge(row.call_status) === "light" ? "dark" : undefined}>
                              {titleCase(row.call_status)}
                            </Badge>
                            <TechnicalId value={row.call_sid} error={row.call_error} label="call ID" />
                          </td>
                          <td>{row.call_duration ? `${row.call_duration} sec` : "—"}</td>
                          <td>{formatDate(row.created_at)}</td>
                          <td>{formatDate(row.updated_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
          {!loading && filteredRows.length > 0 ? (
            <Card.Footer className="twilio-pagination">
              <span>
                Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filteredRows.length)} of {filteredRows.length}
              </span>
              <div>
                <Button size="sm" variant="light" disabled={safePage === 1} onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}>Previous</Button>
                <span>Page {safePage} of {totalPages}</span>
                <Button size="sm" variant="light" disabled={safePage === totalPages} onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}>Next</Button>
              </div>
            </Card.Footer>
          ) : null}
        </Card>
      </Container>
    </div>
  );
}
